import { useTranslation } from 'react-i18next';
import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import {
  DeleteDialog,
  Iconify,
  makeConfig,
  PaginatedTable2,
  TruncatedTooltipText,
} from '@pflegenavi/web-components';
import { Typography } from '@mui/material';
import type { LineItemConfigurationControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useFormatting } from '@pflegenavi/frontend/localization';

type ColumnNames = 'serviceName' | 'amount' | 'taxRate' | 'taxName' | 'notes';
type Filters = 'never';

interface ServicesOverviewTableViewOverviewTableViewProps {
  data: LineItemConfigurationControllerResultTypes['getLineItemConfigurations'];
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  isLoading: boolean;
  handleDeleteTaxLineConfig: (id: string) => Promise<void>;
}

export interface ServicesOverviewTableViewRowModel {
  id: string;
  serviceName: string;
  amountString: string;
  taxRateString: string;
  taxName: string;
  notes: string;
}

export const ServicesOverviewTableViewOverviewTableView: FC<
  ServicesOverviewTableViewOverviewTableViewProps
> = ({
  data,
  isLoading,
  onChangePageSize,
  page,
  pageSize,
  onChangePage,
  handleDeleteTaxLineConfig,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);

  const transformed = useMemo(() => {
    return data
      .map<ServicesOverviewTableViewRowModel>((row) => {
        const taxRateString =
          row.taxCodes && row.taxCodes.length > 0
            ? `${(row.taxCodes[0].taxAmount * 100).toString()}%`
            : '--';
        return {
          id: row.id,
          serviceName: row.name,
          amountString: fCurrencyCents(row.amount),
          taxRateString,
          taxName: row.taxCodes?.[0]?.taxName ?? '--',
          notes: row.taxCodes?.[0]?.explanationLong || '--',
        };
      })
      .slice(page * pageSize, page * pageSize + pageSize);
  }, [data, fCurrencyCents, page, pageSize]);

  const config = useMemo(
    () =>
      makeConfig<ServicesOverviewTableViewRowModel, ColumnNames, Filters>({
        showAll: undefined,
        emptyRowHeight: 56,
        columns: [
          {
            key: 'serviceName',
            label: t(
              'service-provider-page.services.table.columns.service-name'
            ),
            align: 'left',
            component: ({ row }) => (
              <TruncatedTooltipText
                variant="subtitle2"
                text={row.serviceName}
                maxWidth={330}
              />
            ),
          },
          {
            key: 'amount',
            label: t('service-provider-page.services.table.columns.amount'),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2">{row.amountString}</Typography>
            ),
          },
          {
            key: 'taxRate',
            label: t('service-provider-page.services.table.columns.tax-rate'),
            align: 'right',
            component: ({ row }) => {
              return (
                <Typography variant="subtitle2">{row.taxRateString}</Typography>
              );
            },
          },
          {
            key: 'taxName',
            label: t('service-provider-page.services.table.columns.tax-name'),
            align: 'left',
            component: ({ row }) => {
              return <Typography variant="subtitle2">{row.taxName}</Typography>;
            },
          },
          {
            key: 'notes',
            label: t('service-provider-page.services.table.columns.note'),
            align: 'left',
            component: ({ row }) => (
              <TruncatedTooltipText
                variant="subtitle2"
                text={row.notes}
                maxWidth={270}
              />
            ),
          },
        ],
        options: {
          items: [
            {
              key: 'delete',
              label: t('actions.delete'),
              errorVariant: true,
              icon: <Iconify icon={'eva:trash-2-outline'} />,
              onClick: (row) => {
                setIdToDelete(row.id);
                setDeleteModalOpen(true);
              },
            },
          ],
        },
        pagination: {
          page,
          pageSize,
          pageSizeOptions: [5, 10, 15, 20, 25],
          total: data.length,
          onPageChange: onChangePage,
          onPageSizeChange: onChangePageSize,
        },
      }),
    [data.length, onChangePage, onChangePageSize, page, pageSize, t]
  );

  return (
    <>
      <PaginatedTable2
        name="ServicesOverviewTable"
        config={config}
        rows={transformed}
        isLoading={isLoading}
      />
      <DeleteDialog
        open={deleteModalOpen}
        setDeleteDialogOpen={setDeleteModalOpen}
        maxWidth="sm"
        title={t('service-provider-page.services.delete-modal.title')}
        confirmationMessage={t(
          'service-provider-page.services.delete-modal.description'
        )}
        handleDelete={async () => {
          if (idToDelete) {
            await handleDeleteTaxLineConfig(idToDelete);
          }
          setIdToDelete(undefined);
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
};
